import { useState, useEffect, useCallback, useRef } from 'react';
import { ApiClient } from '../api/client';
import { useWebApp } from '../webAppContext';
import { UserInfo, UserConfig, TelegramUser } from '../types';
import { messages } from '../messages';
import * as Sentry from "@sentry/react";
import { debugLog } from '../utils/logger';
import posthog from 'posthog-js';

interface UseAuthResult {
  userInfo: UserInfo | null;
  userConfig: UserConfig | null;
  error: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  acceptInvitation: (invitationId: string) => Promise<void>;
  updateUserInfo: (newUserInfo: UserInfo) => void;
  updateUserConfig: (newUserConfig: UserConfig) => void;
  completeOnboarding: () => Promise<void>;
}

export function useAuth(): UseAuthResult {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [userConfig, setUserConfig] = useState<UserConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const webApp = useWebApp();
  const hasAuthenticated = useRef(false);

  const updateUserInfo = useCallback((newUserInfo: UserInfo) => {
    setUserInfo(newUserInfo);
  }, []);

  const updateUserConfig = useCallback((newUserConfig: UserConfig) => {
    setUserConfig(newUserConfig);
  }, []);

  const completeOnboarding = useCallback(async () => {
    setUserConfig({
      onboarding_completed: true,
      onboarding_completed_at: new Date().toISOString(),
      telegram_file_type: 'document' // Default value
    });
    posthog.capture('onboarding_completed');
    return Promise.resolve();
  }, []);

  const acceptInvitation = useCallback(async (invitationId: string) => {
    if (!userInfo) return;
    
    try {
      await ApiClient.acceptInvitation(invitationId);
      const updatedUserInfo = await ApiClient.getUserInfo();
      setUserInfo(updatedUserInfo);
      
      if (updatedUserInfo.activated) {
        await webApp.showPopup({
          title: messages.welcome.title,
          message: messages.welcome.invitationAccepted,
          buttons: [{ type: 'ok' }]
        });
      }
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      throw error;
    }
  }, [userInfo, webApp]);

  // Store latest acceptInvitation in a ref so useEffect doesn't depend on it
  const acceptInvitationRef = useRef(acceptInvitation);
  useEffect(() => {
    acceptInvitationRef.current = acceptInvitation;
  }, [acceptInvitation]);
  
  useEffect(() => {
    if (!webApp.isReady || hasAuthenticated.current) {
      return;
    }

    // Use a ref to track if component is mounted
    let isMounted = true;
    
    // Debug output for WebApp state
    debugLog('WebApp ready state:', webApp.isReady);
    debugLog('WebApp initData exists:', Boolean(webApp.initData));
    debugLog('WebApp initDataUnsafe exists:', Boolean(webApp.initDataUnsafe));
    debugLog('WebApp user exists:', Boolean(webApp.initDataUnsafe?.user));
    if (webApp.initDataUnsafe?.user) {
      // Log basic user data for debugging
      debugLog('User data available in WebApp');
      
      // Note: In Telegram test environments, photo_url might not be available
      // This is expected and the app should use the default avatar in these cases
    }
    
    // For development debugging - allowing local testing without Telegram
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    const authenticateUser = async () => {
      debugLog('Starting authentication process...');
      
      try {
        // In development, we might want to proceed without real Telegram data
        const telegramUser = webApp.initDataUnsafe?.user as TelegramUser | undefined;
        
        if (!telegramUser) {
          console.error('No Telegram user data found');
          
          if (isDevelopment) {
            console.warn('Development mode: Creating mock user for testing');
            
            // Create mock data for local development
            const mockResult = { access_token: 'dev-token' };
            localStorage.setItem('token', mockResult.access_token);
            
            if (isMounted) {
              setIsAuthenticated(true);
              
              // Mock user data
              const mockUserInfo = {
                id: 123456789,
                username: 'dev_user',
                first_name: 'Dev',
                last_name: 'User',
                photo_url: 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y',
                activated: true,
                balance: 1000
              };
              
              // Mock config
              const mockConfig = {
                onboarding_completed: true,
                onboarding_completed_at: new Date().toISOString(),
                telegram_file_type: 'document' as const
              };
              
              setUserInfo(mockUserInfo);
              setUserConfig(mockConfig);
              setIsLoading(false);
            }
            return;
          } else {
            // In production, show an error
            if (isMounted) {
              setError('Please open this app through Telegram');
              setIsLoading(false);
            }
            return;
          }
        }

        // Helper function for delays
        const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
        
        try {
          // Authenticate with backend using Telegram data
          // Extract photo URL if available in Telegram data
          const photoUrl = telegramUser.photo_url;
          
          const result = await ApiClient.authenticate(webApp.initData, {
            id: telegramUser.id,
            username: telegramUser.username || '',
            first_name: telegramUser.first_name,
            last_name: telegramUser.last_name,
            photo_url: photoUrl
          });
          
          localStorage.setItem('token', result.access_token);
          if (!isMounted) return;
          setIsAuthenticated(true);
          
          // Small delay to prevent UI flashing
          await delay(50);
          if (!isMounted) return;
          
          // Get user info and config in parallel for better performance
          const [userInfoResult, userConfigResult] = await Promise.all([
            ApiClient.getUserInfo(),
            ApiClient.getUserConfig()
          ]);
          
          if (!isMounted) return;
          
          // Update state with fetched data
          setUserInfo(userInfoResult);
          setUserConfig(userConfigResult);

          // PostHog: Identify user and set user properties
          if (userInfoResult) {
            posthog.identify(
              userInfoResult.id.toString(), // Ensure ID is a string
              {
                username: userInfoResult.username,
                first_name: userInfoResult.first_name,
                last_name: userInfoResult.last_name,
                photo_url: userInfoResult.photo_url,
                activated: userInfoResult.activated,
                // Add any other user properties you want to track
              }
            );
          }
          if (userConfigResult) {
            posthog.people.set({
              onboarding_completed: userConfigResult.onboarding_completed,
              onboarding_completed_at: userConfigResult.onboarding_completed_at,
              // Add any other user config properties
            });
          }

          // Handle invitation if present
          const startApp = webApp.initDataUnsafe?.start_param;
          const invitationId = startApp?.startsWith('invite_') ? startApp.replace('invite_', '') : null;
          
          if (invitationId && !userInfoResult.activated && isMounted) {
            await acceptInvitationRef.current(invitationId);
          }
          
        } catch (apiError) {
          console.error('API error during authentication:', apiError);
          if (isMounted) {
            const errorMessage = apiError instanceof Error ? apiError.message : 'API error during authentication';
            setError(errorMessage);
            
            // Report API errors to Sentry
            Sentry.captureException(apiError, {
              tags: {
                location: 'useAuth',
                process: 'authentication'
              },
              extra: {
                telegramUserId: telegramUser?.id
              }
            });
          }
        } finally {
          // Always mark loading as complete when done
          if (isMounted) {
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error('Authentication process failed:', error);
        if (isMounted) {
          const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
          setError(errorMessage);
          
          // Report general authentication errors to Sentry
          Sentry.captureException(error, {
            tags: {
              location: 'useAuth',
              process: 'authentication_outer'
            }
          });
          
          setIsLoading(false);
        }
      }
    };

    // Start authentication process
    hasAuthenticated.current = true;
    authenticateUser();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [webApp.isReady, webApp.initData, webApp.initDataUnsafe, webApp.colorScheme]);

  return {
    userInfo,
    userConfig,
    error,
    isLoading,
    isAuthenticated,
    acceptInvitation,
    updateUserInfo,
    updateUserConfig,
    completeOnboarding
  };
}